/**
 * Reactive-Element property-shadowing mixin.
 * Copies one or more reactive properties from this reactive-element
 * to another object or element when changed.
 * @template { import('@nrk/reactive-element').ReactiveElementBase } BaseClass
 * @param { BaseClass } Base
 */
export function ShadowPropertiesMixin(Base) {
  return class extends Base {
    /**
     * Constructor
     * @param  {...any} args
     */
    constructor(...args) {
      super(...args);
      /** @type { { [propName: string]: Map<unknown, string> } } */
      this._propertyChangeObservers = {};
    }

    /**
     * Shadow this element's reactive "propNames", and copy them to "target" when changed.
     * Returns function to unshadow properties.
     * @param { string | Array<string> } propNames
     * @param { unknown } target
     * @returns { () => void }
     */
    shadowProperties(propNames, target) {
      const constructor = /** @type { typeof import('@nrk/reactive-element').ReactiveElement } */ (this.constructor);
      const reactiveAttributes = constructor.reactiveAttributes;
      const reactiveProperties = constructor.reactiveProperties;

      if (!Array.isArray(propNames)) {
        propNames = [propNames];
      }

      for (const propName of propNames) {
        const [originalPropName, localPropName = originalPropName] = propName.split(':');

        if (
          reactiveProperties.attributes &&
          !(originalPropName in reactiveAttributes) &&
          reactiveProperties.properties &&
          !(originalPropName in reactiveProperties)
        ) {
          throw Error(`${originalPropName} is not a reactive property and cannot be shadowed`);
        }

        if (!(originalPropName in this._propertyChangeObservers)) {
          this._propertyChangeObservers[originalPropName] = new Map();
        }

        this._propertyChangeObservers[originalPropName].set(target, localPropName);

        // @ts-ignore
        target[localPropName] = this[originalPropName];
      }

      const unshadow = () => {
        for (const propName of propNames) {
          const originalPropName = propName.split(':')[0];

          this._propertyChangeObservers[originalPropName].delete(target);
        }
      };

      return unshadow;
    }

    /**
     * @param { string } propName
     * @param { unknown } oldValue
     */
    updating(propName, oldValue) {
      // Handle ReactiveState property changes (which are namespaced)
      const propNamespace = propName.split('.')[0];
      const isReactiveState = propNamespace !== propName;

      if (propNamespace in this._propertyChangeObservers) {
        for (const [target, localPropName] of this._propertyChangeObservers[propNamespace]) {
          // Skip setting ReactiveState property if already set since changes are already subscribed to
          // @ts-ignore
          if (isReactiveState && target[propNamespace] === this[propNamespace]) {
            continue;
          }
          // @ts-ignore
          target[localPropName] = this[propNamespace];
        }
      }

      super.updating(propName, oldValue);
    }
  };
}
